import { createSelector } from 'reselect';
const caseIdInProps = (state, props) => props.caseId;
const caseInfoCaseId = (state) => state.screens.cases.caseInfoCaseId;
const getCurrentCaseId = (state) => state.application.currentCaseId;
const getCases = (state) => state.entities.cases;
const getPatients = (state) => state.entities.patients;
const getFollowers = (state) => state.entities.followers;
const componentsUploads = (state) => state.screens.components.uploads;
const getFiles = (state) => state.entities.files;
const getCurrentUserId = (state) => state.user.userId;
const getUserProfile = (state) => state.entities.userProfile;

import roleTypeEnum from '../../constants/participant_roles';

import { getEmployers, getEmployerPermissions } from '../userProfile/selector';
import { getInboundConsultReferrals } from '../referrals/selector';

import {
  FAMILY_MEMBER_PERMISSIONS,
  PERSONAL_CASE_PERMISSIONS,
  USERS_CASE_OVERRIDE_PERMISSIONS,
} from '../../constants/permissions';

import { CASE_AVATAR } from '../../constants/objectTypes';
import { fileComponentKey as cKey } from '../../helpers/reducers';

function _getCaseFiles(files, caseId) {
  let fileCKey = cKey({
    objectType: CASE_AVATAR,
    objectId: caseId,
  });

  return (files[fileCKey] && files[fileCKey].files) || null;
}

function _caseWithFilesAttached(files, theCase) {
  if (!theCase) return null;

  return {
    ...theCase,
    avatar: (files && files.length && files[files.length - 1]) || null,
  };
}

function _caseObject(caseId, cases) {
  if (caseId && cases[caseId]) {
    return Object.freeze(cases[caseId]);
  }
  return null;
}

function _casePatient(patients, editingCase) {
  if (editingCase == null) return null;
  if (editingCase.patientId == null) return null;
  return patients[editingCase.patientId];
}

// get the files attached to a case
const getCurrentCaseFiles = createSelector(getFiles, getCurrentCaseId, _getCaseFiles);
const getEditingCaseFiles = createSelector(getFiles, caseInfoCaseId, _getCaseFiles);

/**
 * Gets the case object based on the currently selected case.
 * Useful to display name of the case in some portions of the app
 * Memoized on current case id, cases
 */
export const getCurrentCase = createSelector(getCurrentCaseId, getCases, _caseObject);
export const getEditingCase = createSelector(caseInfoCaseId, getCases, _caseObject);
export const getCaseById = createSelector(caseIdInProps, getCases, _caseObject);

// returns a case object with files included
export const getCurrentCaseWithFiles = createSelector(
  getCurrentCaseFiles,
  getCurrentCase,
  _caseWithFilesAttached
);
export const getEditingCaseWithFiles = createSelector(
  getEditingCaseFiles,
  getEditingCase,
  _caseWithFilesAttached
);

// returns the patient associated with a case
export const getEditingCasePatient = createSelector(getPatients, getEditingCase, _casePatient);
export const getCurrentCasePatient = createSelector(getPatients, getCurrentCase, _casePatient);

// TODO: could add sorting here
function _participantsArray(employer, userId, participants, followers) {
  if (!participants) return Object.freeze([]);

  return Object.keys(participants).map((k) => {
    const participant = participants[k];
    const profile = { ...followers[k] };
    if (employer && profile.employers) {
      const participantEmployer = profile.employers[employer.employerId];

      if (participantEmployer) {
        profile.roleTitle = participantEmployer.role;
        profile.medicalService = participantEmployer.medicalService;
        profile.clinicalLocation = participantEmployer.clinicalLocation;
      }
    }
    return {
      participant,
      profile,
    };
  });
}

/**
 * Gets the participants property current case
 * Memoized on current case object
 */
export const getCurrentCaseParticipantsProperty = createSelector(getCurrentCase, (c) => {
  if (!c) return null;
  return c.participants;
});
/**
 * Gets the participants property for the editing case
 */
const getEditingCaseParticipantsProperty = createSelector(getEditingCase, (c) => {
  if (!c) return null;
  return c.participants;
});

function _employerForCase(employers, theCase) {
  if (!theCase || !theCase.employerId) return null;

  let e = (employers && employers[theCase.employerId]) || null;
  return e;
}
export const getCurrentCaseEmployer = createSelector(
  getEmployers,
  getCurrentCase,
  _employerForCase
);
export const getEditingCaseEmployer = createSelector(
  getEmployers,
  getEditingCase,
  _employerForCase
);

export const getCurrentCaseEmployerType = createSelector(
  getCurrentCase,
  getEmployers,
  (currentCase, employers) => {
    if (!currentCase || !currentCase.employerId || !employers) return null;
    const employer = employers[currentCase.employerId];
    return employer?.uiConfig?.type || null;
  }
);
export const getEditingCaseEmployerType = createSelector(
  getEditingCase,
  getEmployers,
  (editingCase, employers) => {
    if (!editingCase || !editingCase.employerId || !employers) return null;
    const employer = employers[editingCase.employerId];
    return employer?.uiConfig?.type || null;
  }
);
export const getEditingCaseEmployerMedicalServices = createSelector(
  getEditingCase,
  getEmployers,
  (editingCase, employers) => {
    if (!editingCase || !editingCase.employerId || !employers) return null;
    const employer = employers[editingCase.employerId];
    return employer?.uiConfig?.medicalServices;
  }
);
export const getEditingCaseEmployerClinicalLocations = createSelector(
  getEditingCase,
  getEmployers,
  (editingCase, employers) => {
    if (!editingCase || !editingCase.employerId || !employers) return null;
    const employer = employers[editingCase.employerId];
    return employer?.uiConfig?.clinicalLocations;
  }
);
export const getEditingCasePatientEmployerUser = createSelector(
  getEditingCasePatient,
  getEditingCaseEmployer,
  getFollowers,
  (patient, employer, followers) => {
    const patientUserId = patient?.userId;
    const patientFollower = patientUserId && followers[patientUserId];
    return patientFollower && employer && patientFollower.employers?.[employer.employerId];
  }
);
export const getEditingCaseConsult = createSelector(
  getEditingCase,
  getInboundConsultReferrals,
  (editingCase, consults) => {
    return consults.find(
      (c) => c.case_id === editingCase.caseId && c.request_status === 'accepted'
    );
  }
);

/**
 * getEditingCaseParticipants, getCurrentCaseParticipants
 * return value is an object with
 * properties: {participant, profile} (redux state objects from
 * case.participants and entities.followers).
 * participant property is guaranteed to have a value
 * profile property SHOULD have a value unless something has gone wrong and
 * entities.followers has gotten out of sync with case participants
 */
/**
 * Gets the participants for the editing case
 * Memoized on the participants property of the editing case
 */
export const getEditingCaseParticipants = createSelector(
  getEditingCaseEmployer,
  getCurrentUserId,
  getEditingCaseParticipantsProperty,
  getFollowers,
  _participantsArray
);
/**
 * Gets the participants for the current case
 * Memoized on the participants property of the current case
 */
export const getCurrentCaseParticipants = createSelector(
  getCurrentCaseEmployer,
  getCurrentUserId,
  getCurrentCaseParticipantsProperty,
  getFollowers,
  _participantsArray
);

function _participantsArrayExcludingCurrentUser(participants, userId) {
  // filter out current user and
  // filter out those with userId === -1 which are Outpatient Admin accounts
  return participants.filter((item) => {
    return item.participant.userId !== userId && item.participant.userId > 0;
  });
}
/**
 * Gets the participants for the editing case
 * Memoized on the participants property of the editing case
 */
export const getEditingCaseParticipantsExcludingCurrentUser = createSelector(
  getEditingCaseParticipants,
  getCurrentUserId,
  _participantsArrayExcludingCurrentUser
);
/**
 * Gets the participants for the current case
 * Memoized on the participants property of the current case
 */
export const getCurrentCaseParticipantsExcludingCurrentUser = createSelector(
  getCurrentCaseParticipants,
  getCurrentUserId,
  _participantsArrayExcludingCurrentUser
);

/** get the participant for the current user **/
export const getCurrentCaseCurrentUserParticipant = createSelector(
  getCurrentCaseParticipants,
  getCurrentUserId,
  (participants, userId) => {
    return participants.filter((item) => {
      return item.participant.userId === userId;
    })[0];
  }
);

export const getEditingCaseSupervisor = createSelector(
  getEditingCaseParticipants,
  (participants) => {
    return participants.find((p) => p.participant?.roleId === roleTypeEnum.SUPERVISOR);
  }
);
export const getCurrentCaseSupervisor = createSelector(
  getCurrentCaseParticipants,
  (participants) => {
    return participants.find((p) => p.participant?.roleId === roleTypeEnum.SUPERVISOR);
  }
);

function _caseParticipantsExcludingPatient(c, participants, patientsById) {
  const editingCasePatientUserId = c?.patientId && patientsById[c.patientId]?.userId;
  return participants.filter((p) => {
    const userId = p.participant?.userId;
    if (editingCasePatientUserId && editingCasePatientUserId === userId) return false;
    return true;
  });
}
export const getCurrentCaseParticipantsExcludingPatient = createSelector(
  getCurrentCase,
  getCurrentCaseParticipants,
  getPatients,
  _caseParticipantsExcludingPatient
);
export const getEditingCaseParticipantsExcludingPatient = createSelector(
  getEditingCase,
  getEditingCaseParticipants,
  getPatients,
  _caseParticipantsExcludingPatient
);

export const getEditingCasePatientProfile = createSelector(
  getEditingCasePatient,
  getFollowers,
  (patient, followers) => {
    return patient?.userId ? followers[patient.userId] : null;
  }
);

export const getFileUploadStatusForEditingCase = createSelector(
  caseInfoCaseId,
  componentsUploads,
  (caseId, uploads) => {
    if (!caseId) return {};
    let fileCKey = cKey({
      objectType: CASE_AVATAR,
      objectId: caseId,
    });
    return uploads[fileCKey] || {};
  }
);

function _applyCurrentUserOverrides(perms, casePatient, userProfile) {
  if (!perms) return {};
  if (casePatient && userProfile && casePatient.userId === userProfile.userId) {
    const permOverrides = {};
    Object.keys(USERS_CASE_OVERRIDE_PERMISSIONS).forEach((k) => {
      // if the user doesnt have true permission then override with the override val
      // which is usually view only. If they have true set then dont override because this
      // means they already have edit permission for this perm
      permOverrides[k] = perms[k] || USERS_CASE_OVERRIDE_PERMISSIONS[k];
    });

    return {
      ...perms,
      ...permOverrides,
    };
  }
  return perms;
}
function _permissionsForCase(employers, theCase, casePatient, userProfile) {
  if (!theCase) return null;

  // the case is not associated with an employer org
  if (!theCase.employerId) {
    return _applyCurrentUserOverrides(PERSONAL_CASE_PERMISSIONS, casePatient, userProfile);
  }

  // if the case is associated with an employer org, but the user is not
  // on the staff (employer data is not in their userProfile)
  const employer = employers && employers[theCase.employerId];
  if (!employer || employer.external === true || employer.external === undefined) {
    return _applyCurrentUserOverrides(FAMILY_MEMBER_PERMISSIONS, casePatient, userProfile);
  }

  return _applyCurrentUserOverrides(employer.permissions, casePatient, userProfile);
  // return TEST_PERMISSIONS;
}
export const getCurrentCasePermissions = createSelector(
  getEmployers,
  getCurrentCase,
  getCurrentCasePatient,
  getUserProfile,
  _permissionsForCase
);
export const getEditingCasePermissions = createSelector(
  getEmployers,
  getEditingCase,
  getEditingCasePatient,
  getUserProfile,
  _permissionsForCase
);

export const getEditingCaseEmployerFeatures = createSelector(getEditingCaseEmployer, (employer) => {
  return (employer && employer.uiConfig && employer.uiConfig.features) || {};
});
export const getEditingCaseEmployerRole = createSelector(getEditingCaseEmployer, (employer) => {
  return employer?.role || null;
});
